import { Tournament } from '@omniafishing/core';
import { Col, Divider, Row } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import React from 'react';
import { Link } from 'react-router-dom';
import { stripHtml } from '../../lib/html_strip';
import { OmniaUrls } from '../../lib/urls';
import base from '../../styles/base.less';
import { ContentWrapper } from '../content_wrapper/content_wrapper';
import { SEO } from '../seo/seo';
import { waterbodyImgUrl } from '../waterbody_img/waterbody_img';
import styles from './tournament_detail.less';

interface TournamentDetailProps {
  fullUrl: string;
  tournament: Tournament;
  isMobile: boolean;
}

function dateISO(date: Date) {
  if (!date) {
    return null;
  }

  return `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}-${(
    '0' + date.getDate()
  ).slice(-2)}`;
}

export const TournamentDetail = (props: TournamentDetailProps) => {
  const { tournament, isMobile, fullUrl } = props;

  function getSchema() {
    const startDateISO = dateISO(new Date(start_date));
    const endDateISO = dateISO(new Date(end_date));

    const schema: any = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: title,
      url: fullUrl,
      startDate: startDateISO,
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    };
    if (endDateISO) {
      schema.endDate = endDateISO;
    }
    if (description) {
      schema.description = metaDescription;
    }
    if (waterbodies.length > 0) {
      const firstWaterbody = waterbodies[0];
      schema.location = {
        '@type': 'Place',
        name: firstWaterbody.primary_name,
        address: {
          '@type': 'PostalAddress',
        },
        geo: {
          '@type': 'GeoCoordinates',
          longitude: firstWaterbody.lng,
          latitude: firstWaterbody.lat,
        },
      };
    }

    return (
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
    );
  }

  function dateRange() {
    if (!start_date) {
      return end_date;
    } else if (!end_date) {
      return start_date;
    } else if (start_date === end_date) {
      if (end_date) {
        return end_date;
      }
      return start_date;
    } else {
      const startDateWithoutYear = start_date.split('/').splice(0, 2).join('/');
      return `${startDateWithoutYear}–${end_date}`;
    }
  }

  if (!tournament) {
    return null;
  }

  const {
    contact,
    description,
    email,
    end_date,
    entry_fee,
    estimated_boats,
    estimated_entrants,
    estimated_prize,
    organization,
    organization_other,
    permit_number,
    phone,
    series_name,
    species,
    start_date,
    time_descriptions,
    title,
    url,
    waterbodies,
    results_recap,
    launch,
    qualification_info,
  } = tournament;

  const waterbody = waterbodies[0];
  const size = isMobile ? { width: 400, height: 250 } : { width: 1280, height: 300 };

  const waterbodyText = waterbody ? ` at ${waterbody.primary_name}` : '';
  const metaDescription = `Learn more about this fishing tournament${waterbodyText} on ${dayjs(
    start_date
  ).format('MMM D, YYYY')} from Omnia Fishing. ${stripHtml(description)}`;

  return (
    <section>
      <SEO
        title={title}
        description={metaDescription}
        ogImage={waterbodyImgUrl(waterbody, {
          imgSize: { width: size.width, height: size.height },
          hires: true,
        })}
        ogType={'event'}
        ogEventStart={dayjs(start_date).toISOString()}
        ogEventPlace={waterbody?.primary_name}
      />

      {waterbody && (
        <div className={classNames(base.contentWrapper, base.contentWrapper__mobileNoPadding)}>
          <div className={styles.imgWrapper}>
            <img
              src={waterbodyImgUrl(waterbody, {
                imgSize: { width: size.width, height: size.height },
                hires: true,
              })}
              alt={`${waterbody.primary_name} Map`}
            />
            <Link
              to={OmniaUrls.waterbody(waterbody)}
              className={classNames('ant-btn', 'ant-btn-lg', 'ant-btn-primary', styles.lakeButton)}
            >
              Go to Lake Page
            </Link>
          </div>
        </div>
      )}

      <ContentWrapper>
        <article className={styles.article}>
          <div className={styles.main}>
            <h1 className={styles.h1}>{title}</h1>
            {series_name && <h2 className={styles.series_name}>{series_name}</h2>}

            <div className={styles.dates_recap}>
              <div>
                <p className={styles.dateRange}>{dateRange()}</p>
                {time_descriptions && (
                  <div dangerouslySetInnerHTML={{ __html: time_descriptions }} />
                )}
              </div>

              {results_recap && (
                <div className={styles.resultsRecap}>
                  <h4 className={styles.resultsRecapHeading}>Results Recap</h4>
                  <div
                    className={styles.resultsRecapHtml}
                    dangerouslySetInnerHTML={{ __html: results_recap }}
                  />
                </div>
              )}
            </div>
            <Divider />
            <div className={styles.detailGrid}>
              {waterbodies.length > 0 && (
                <div className={styles.detailCell}>
                  <h2 className={styles.detailHeading}>Waterbodies:</h2>
                  <ul className={styles.waterbodiesList}>
                    {waterbodies.map((w) => (
                      <li key={w.waterbody_id}>
                        <Link to={OmniaUrls.waterbody(w)}>{w.primary_name}</Link>
                      </li>
                    ))}
                  </ul>
                  {launch && (
                    <>
                      <h2 className={styles.detailHeading}>Launch:</h2>
                      <p>{launch}</p>
                    </>
                  )}
                </div>
              )}
              {(entry_fee || estimated_prize) && (
                <div className={styles.detailCell}>
                  <h2 className={styles.detailHeading}>Entry Fee:</h2>
                  <p>{entry_fee}</p>
                  <h2 className={styles.detailHeading}>Estimated Prize:</h2>
                  <p>{estimated_prize}</p>
                </div>
              )}
              {(estimated_entrants || estimated_boats) && (
                <div className={styles.detailCell}>
                  <h2 className={styles.detailHeading}>Estimated # of Entrants:</h2>
                  <p>{estimated_entrants}</p>
                  <h2 className={styles.detailHeading}>Estimated # of Boats:</h2>
                  <p>{estimated_boats}</p>
                </div>
              )}
            </div>
            <Divider />
            <div className={styles.bottom}>
              <Row>
                {description && (
                  <Col xs={24} sm={18}>
                    <h2 className={styles.detailHeading}>Overview</h2>
                    <div
                      className={styles.description}
                      dangerouslySetInnerHTML={{ __html: description }}
                    />
                  </Col>
                )}

                {species.length > 0 && (
                  <Col xs={24} sm={6}>
                    <div className={styles.species}>
                      <h2 className={styles.detailHeading}>Species:</h2>
                      <ul className={styles.speciesList}>
                        {species.map((specie) => (
                          <li key={specie.name}>{specie.display_name}</li>
                        ))}
                      </ul>
                    </div>
                  </Col>
                )}
              </Row>

              {qualification_info && (
                <div>
                  <h2 className={styles.detailHeading}>Qualification Info</h2>
                  <p>{qualification_info}</p>
                </div>
              )}
            </div>
          </div>

          <aside className={styles.sidebar}>
            {url && (
              <>
                <h3 className={styles.detailMinorHeading}>Website</h3>
                <p>
                  <a href={url} rel="nofollow">
                    View Tournament Website
                  </a>
                </p>
              </>
            )}
            {(organization || organization_other) && (
              <>
                <h3 className={styles.detailMinorHeading}>Organizing Group</h3>
                <p>
                  {organization ? (
                    <a href={OmniaUrls.organization(organization)}>{organization.name}</a>
                  ) : (
                    organization_other
                  )}
                </p>
              </>
            )}
            {contact && (
              <>
                <h3 className={styles.detailMinorHeading}>Contact Name</h3>
                <p>{contact}</p>
              </>
            )}
            {phone && (
              <>
                <h3 className={styles.detailMinorHeading}>Contact Phone</h3>
                <p>
                  <a href={`tel:${phone}`}>{phone}</a>
                </p>
              </>
            )}
            {email && (
              <>
                <h3 className={styles.detailMinorHeading}>Contact Email</h3>
                <p>
                  <a href={`mailto:${email}`}>{email}</a>
                </p>
              </>
            )}
            {permit_number && (
              <>
                <h3 className={styles.detailMinorHeading}>Permit #</h3>
                <p>{permit_number}</p>
              </>
            )}
          </aside>
        </article>
      </ContentWrapper>
      {getSchema()}
    </section>
  );
};
